import React from 'react';
import { CheckCircle, Play } from 'lucide-react';
import { format } from 'date-fns';

export default function ApprovalWorkflowPanel({ refinements, onApply, onUpdateStatus, onRefresh }) {
  const handleApplyRefinement = async (refinementId) => {
    await onApply(refinementId);
  };

  const getTypeLabel = (type) => {
    const labels = {
      'faq_add': 'Ajout FAQ',
      'faq_update': 'Mise à jour FAQ',
      'faq_delete': 'Suppression FAQ',
      'knowledge_update': 'Mise à jour Connaissances',
      'product_update': 'Mise à jour Produit',
      'rule_update': 'Mise à jour Règle'
    };
    return labels?.[type] || type;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Refinements Approuvés Prêts à Appliquer</h3>
        <button
          onClick={onRefresh}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          Actualiser
        </button>
      </div>
      {refinements?.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <CheckCircle className="mx-auto text-gray-400 mb-3" size={48} />
          <p className="text-gray-600">Aucun refinement approuvé en attente d'application</p>
        </div>
      ) : (
        <div className="space-y-4">
          {refinements?.map((refinement) => (
            <div key={refinement?.id} className="bg-white rounded-lg border-2 border-green-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <CheckCircle className="text-green-600" size={20} />
                    <span className="text-sm font-semibold text-green-600">APPROUVÉ</span>
                    <span className="text-xs text-gray-500">
                      {getTypeLabel(refinement?.refinement_type)}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{refinement?.title}</h3>
                  <p className="text-sm text-gray-600 mb-2">
                    Approuvé le {format(new Date(refinement.approved_at || refinement.updated_at), 'dd/MM/yyyy à HH:mm')}
                  </p>
                </div>
              </div>

              {refinement?.rationale && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-1">Justification:</p>
                  <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">{refinement?.rationale}</p>
                </div>
              )}

              {refinement?.impact_assessment && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-1">Impact Attendu:</p>
                  <p className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-200">{refinement?.impact_assessment}</p>
                </div>
              )}

              {refinement?.review_notes && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-1">Notes d'Approbation:</p>
                  <p className="text-sm text-gray-600 bg-green-50 p-3 rounded-lg border border-green-200">{refinement?.review_notes}</p>
                </div>
              )}

              {/* Proposed Content Preview */}
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Contenu à Appliquer:</p>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <pre className="text-xs text-gray-800 overflow-x-auto">
                    {JSON.stringify(refinement?.proposed_content, null, 2)}
                  </pre>
                </div>
              </div>

              {/* Apply Button */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => handleApplyRefinement(refinement?.id)}
                  className="flex items-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  <Play size={18} />
                  <span>Appliquer à la Configuration Fatou</span>
                </button>
                <button
                  onClick={() => {
                    const notes = prompt('Raison de l\'annulation:');
                    if (notes) {
                      onUpdateStatus(refinement?.id, 'rejected', notes);
                    }
                  }}
                  className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Annuler l'Approbation
                </button>
              </div>

              {/* Warning */}
              <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-xs text-yellow-800">
                  <strong>⚠️ Attention:</strong> L'application de ce refinement modifiera immédiatement la configuration de Fatou IA. Cette action est irréversible.
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}